
import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  LineChart, 
  Line, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip as RechartsTooltip, 
  Legend, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from "recharts";
import { Button } from "@/components/ui/button";
import { Download, Info, ChevronRight } from "lucide-react";
import { 
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";
import {
  RadioGroup,
  RadioGroupItem,
} from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";

// Sample analytics data
const performanceData = [
  { name: 'Summer Sale', views: 1250, redemptions: 145, uplift: 18 },
  { name: 'Buy 1 Get 1 Free', views: 520, redemptions: 87, uplift: 12 },
  { name: 'Holiday Bundle', views: 800, redemptions: 102, uplift: 15 },
  { name: 'Weekend Special', views: 620, redemptions: 74, uplift: 10 },
];

const timelineData = [
  { date: '05/01', views: 120, redemptions: 10 },
  { date: '05/02', views: 140, redemptions: 12 },
  { date: '05/03', views: 180, redemptions: 18 },
  { date: '05/04', views: 200, redemptions: 22 },
  { date: '05/05', views: 220, redemptions: 28 },
  { date: '05/06', views: 190, redemptions: 25 },
  { date: '05/07', views: 210, redemptions: 30 },
];

const channelData = [
  { name: 'POS', value: 142 },
  { name: 'JimatShop', value: 266 },
];

// Sample detailed data for dialogs
const totalPromotionsDetails = [
  { id: 'PROMO-1001', name: 'Summer Sale', type: '% Discount', status: 'Active' },
  { id: 'PROMO-1002', name: 'Buy 1 Get 1 Free', type: 'Buy X Get Y', status: 'Active' },
  { id: 'PROMO-1003', name: 'Holiday Bundle', type: 'Bundle Deal', status: 'Draft' },
  { id: 'PROMO-1004', name: 'Weekend Special', type: 'Voucher Code', status: 'Expired' }
];

const totalRedemptionsDetails = [
  { id: 'RED-1001', promoName: 'Summer Sale', customer: 'John Doe', date: '2025-05-18', value: 'RM 15.00' },
  { id: 'RED-1002', promoName: 'Buy 1 Get 1 Free', customer: 'Jane Smith', date: '2025-05-19', value: 'RM 10.50' },
  { id: 'RED-1003', promoName: 'Summer Sale', customer: 'Alex Wong', date: '2025-05-20', value: 'RM 15.00' },
  { id: 'RED-1004', promoName: 'Weekend Special', customer: 'Mira Lee', date: '2025-05-20', value: 'RM 8.75' },
];

const totalViewsDetails = [
  { id: 'VIEW-1001', promoName: 'Summer Sale', channel: 'POS', date: '2025-05-18', count: 120 },
  { id: 'VIEW-1002', promoName: 'Buy 1 Get 1 Free', channel: 'JimatShop', date: '2025-05-18', count: 85 },
  { id: 'VIEW-1003', promoName: 'Holiday Bundle', channel: 'POS', date: '2025-05-19', count: 67 },
  { id: 'VIEW-1004', promoName: 'Weekend Special', channel: 'JimatShop', date: '2025-05-20', count: 95 },
];

const COLORS = ['#22c55e', '#3b82f6'];

const PromotionAnalyticsTab: React.FC = () => {
  const [periodFilter, setPeriodFilter] = useState("last7days");
  const [roiTimeFilter, setRoiTimeFilter] = useState("month");
  const [roiTypeFilter, setRoiTypeFilter] = useState("all");
  const [showPromotionsDialog, setShowPromotionsDialog] = useState(false);
  const [showRedemptionsDialog, setShowRedemptionsDialog] = useState(false);
  const [showViewsDialog, setShowViewsDialog] = useState(false);
  
  const getDialogTitle = (type: string) => {
    switch(type) {
      case 'promotions': return 'Total Promotions';
      case 'redemptions': return 'Total Redemptions';
      case 'views': return 'Total Views';
      default: return '';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row gap-4 justify-between items-center">
        <h2 className="text-2xl font-bold">Promotion Analytics Dashboard</h2>
        
        <div className="flex gap-2">
          <Select defaultValue={periodFilter} onValueChange={setPeriodFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Select period" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="last7days">Last 7 Days</SelectItem>
              <SelectItem value="last30days">Last 30 Days</SelectItem>
              <SelectItem value="thisMonth">This Month</SelectItem>
              <SelectItem value="lastMonth">Last Month</SelectItem>
              <SelectItem value="custom">Custom Range</SelectItem>
            </SelectContent>
          </Select>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline">
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Export analytics data</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="hover:border-green-300 cursor-pointer transition-all" onClick={() => setShowPromotionsDialog(true)}>
          <CardHeader className="pb-2">
            <CardDescription className="flex justify-between items-center">
              Total Promotions
              <ChevronRight className="h-4 w-4" />
            </CardDescription>
            <CardTitle className="text-3xl">4</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">2 active, 1 draft, 1 expired</p>
          </CardContent>
        </Card>
        
        <Card className="hover:border-green-300 cursor-pointer transition-all" onClick={() => setShowViewsDialog(true)}>
          <CardHeader className="pb-2">
            <CardDescription className="flex justify-between items-center">
              Total Views
              <ChevronRight className="h-4 w-4" />
            </CardDescription>
            <CardTitle className="text-3xl">3,190</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-green-600">+12.5% from last period</p>
          </CardContent>
        </Card>
        
        <Card className="hover:border-green-300 cursor-pointer transition-all" onClick={() => setShowRedemptionsDialog(true)}>
          <CardHeader className="pb-2">
            <CardDescription className="flex justify-between items-center">
              Total Redemptions
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-4 w-4 ml-1 text-muted-foreground" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p>The total number of times a promotion has been used by customers</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <ChevronRight className="h-4 w-4" />
            </CardDescription>
            <CardTitle className="text-3xl">408</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-green-600">+8.3% from last period</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center">
              Average Uplift
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-4 w-4 ml-1 text-muted-foreground" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p>The average percentage increase in sales for products with active promotions compared to their baseline sales</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </CardDescription>
            <CardTitle className="text-3xl">14.5%</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">Based on sales data</p>
          </CardContent>
        </Card>
      </div>
      
      {/* Promotion Performance Chart */}
      <Card className="col-span-2">
        <CardHeader>
          <CardTitle>Promotion Performance</CardTitle>
        </CardHeader>
        <CardContent className="h-[350px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={performanceData}
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis yAxisId="left" orientation="left" />
              <YAxis yAxisId="right" orientation="right" />
              <RechartsTooltip />
              <Legend />
              <Bar yAxisId="left" dataKey="views" name="Views" fill="#3b82f6" />
              <Bar yAxisId="left" dataKey="redemptions" name="Redemptions" fill="#22c55e" />
              <Bar yAxisId="right" dataKey="uplift" name="Sales Uplift %" fill="#f97316" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Timeline Chart */}
        <Card className="col-span-2">
          <CardHeader>
            <CardTitle>Views & Redemptions Timeline</CardTitle>
          </CardHeader>
          <CardContent className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={timelineData}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <RechartsTooltip />
                <Legend />
                <Line type="monotone" dataKey="views" stroke="#3b82f6" activeDot={{ r: 8 }} />
                <Line type="monotone" dataKey="redemptions" stroke="#22c55e" />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        
        {/* Channel Distribution */}
        <Card>
          <CardHeader>
            <CardTitle>Redemptions by Channel</CardTitle>
          </CardHeader>
          <CardContent className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={channelData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                >
                  {channelData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <RechartsTooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
      
      {/* ROI Analysis with Filters */}
      <Card>
        <CardHeader className="flex flex-row justify-between items-center pb-2">
          <div>
            <CardTitle>ROI Analysis</CardTitle>
            <CardDescription>Impact of promotions on sales and profitability</CardDescription>
          </div>
          <div className="flex gap-2">
            <Select value={roiTimeFilter} onValueChange={setRoiTimeFilter}>
              <SelectTrigger className="w-[120px]">
                <SelectValue placeholder="Time Period" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="week">Weekly</SelectItem>
                <SelectItem value="month">Monthly</SelectItem>
                <SelectItem value="year">Yearly</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={roiTypeFilter} onValueChange={setRoiTypeFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Promotion Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="percent">% Discount</SelectItem>
                <SelectItem value="bxgy">Buy X Get Y</SelectItem>
                <SelectItem value="bundle">Bundle Deals</SelectItem>
                <SelectItem value="voucher">Voucher Codes</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 text-center">
            <div>
              <p className="text-sm text-muted-foreground">Total Discount Value</p>
              <p className="text-2xl font-bold">$2,458.75</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Additional Sales Generated</p>
              <p className="text-2xl font-bold">$8,945.20</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Net Revenue Impact</p>
              <p className="text-2xl font-bold text-green-600">+$6,486.45</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">ROI</p>
              <p className="text-2xl font-bold text-green-600">264%</p>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Dialogs for detailed data */}
      <Dialog open={showPromotionsDialog} onOpenChange={setShowPromotionsDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Total Promotions</DialogTitle>
            <DialogDescription>
              Overview of all created promotions
            </DialogDescription>
          </DialogHeader>
          
          <div className="border rounded-md overflow-auto max-h-96">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {totalPromotionsDetails.map(item => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{item.id}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.type}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        item.status === 'Active' ? 'bg-green-100 text-green-800' :
                        item.status === 'Draft' ? 'bg-gray-100 text-gray-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {item.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <DialogClose asChild>
            <Button type="button" variant="secondary" className="mt-2">Close</Button>
          </DialogClose>
        </DialogContent>
      </Dialog>
      
      <Dialog open={showRedemptionsDialog} onOpenChange={setShowRedemptionsDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Total Redemptions</DialogTitle>
            <DialogDescription>
              Details of all promotion redemptions
            </DialogDescription>
          </DialogHeader>
          
          <div className="border rounded-md overflow-auto max-h-96">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Promotion</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {totalRedemptionsDetails.map(item => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{item.id}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.promoName}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.customer}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.date}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <DialogClose asChild>
            <Button type="button" variant="secondary" className="mt-2">Close</Button>
          </DialogClose>
        </DialogContent>
      </Dialog>
      
      <Dialog open={showViewsDialog} onOpenChange={setShowViewsDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Total Views</DialogTitle>
            <DialogDescription>
              Details of all promotion views
            </DialogDescription>
          </DialogHeader>
          
          <div className="border rounded-md overflow-auto max-h-96">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Promotion</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Channel</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Count</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {totalViewsDetails.map(item => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{item.id}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.promoName}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.channel}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.date}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <DialogClose asChild>
            <Button type="button" variant="secondary" className="mt-2">Close</Button>
          </DialogClose>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PromotionAnalyticsTab;
